import React, { useMemo } from 'react';
import classnames from 'classnames';
import { get, noop, isEmpty, isString, head } from 'lodash';

// local dependencies
import { getLabel } from 'utils/getLabel';
import { map, filter, includes, size } from 'lodash';
import { Icon } from 'modules/icon-bank';
import { radioBox as RadioBox } from 'ui/views/generic/radioBox';

// import { dimensions as BASE_FIELD_WIDTH } from 'config/dimensions/member-auth-criteria-grid';
import { Typeahead } from 'modules/typeahead';
import { DATA_TYPES } from 'constants/common';
import { getDisplayValue } from 'resolvers/formatValue';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { matchCriteria as MatchCriteriaView } from './matchCriteria.view';
 *
 * // render
 * <MatchCriteriaView propName='propValue'/>
 */

/*******************************/

// `radio` field value view
const radioFieldValue = ( props ) => {

    // handle `address` value options
    if( 'address' === props.field.name ) {
        return (
            <div className='field-value-radio'>
                {
                    map( props.value, ( item ) => {
    
                        // get display title
                        const displayTitle = filter( [ item.line1, item.line2, item.city, item.state, item.zip ], Boolean ).join( ', ' );

                        // item class name
                        const itemClassName = classnames( 'field-value-radio__item', {
                            'field-value-radio__item--selected': props.selected === item.id,
                        } );
    
                        return (
                            <div key={ item.id } className={ itemClassName } onClick={ () => props.onSelect( item.id ) }>
                                {/* radio */}
                                <RadioBox name={ item.id } className='field-value-radio__item__checkbox' value={ item.id } selected={ props.selected === item.id } />
    
                                {/* display value */}
                                <div className='field-value-radio__item__title'>{ displayTitle }</div>
                            </div>
                        );
                    } )
                }
            </div>
        );
    }

    /*--------*/

    // normal `text` value options
    return (
        <div className='field-value-radio'>
            {
                map( props.value, ( item ) => {

                    // item class name
                    const itemClassName = classnames( 'field-value-radio__item', {
                        'field-value-radio__item--selected': props.selected === item,
                    } );
                    
                    return (
                        <div key={ item } className={ itemClassName } onClick={ () => props.onSelect( item ) }>
                            {/* radio */}
                            <RadioBox name={ item } className='field-value-radio__item__checkbox' value={ item } selected={ props.selected === item } />

                            {/* display value */}
                            <div className='field-value-radio__item__title'>{ item }</div>
                        </div>
                    );
                } )
            }
        </div>
    );
};

// set display name
radioFieldValue.displayName = 'matchCriteria.radioFieldValue';

// set prop types
radioFieldValue.defaultProps = {
    value: [],
    selected: null,
    field: {},
    onSelect: noop,
};

/*--------*/

// `search` field value view
const searchFieldValue = ( props ) => {
    return (
        <div className='field-value-search'>

            {/* typeahead */}
            <Typeahead
                className='field-value-search__typeahead'
                size='extra-small'
                url={ props.field.url }
                onChange={ ( { selected } ) => {
                    props.onSelect( head( selected ) ); // update selected value
                } }
            />

            {/* display selected value */}
            {
                isEmpty( props.selected ) ? null : (
                    <div className='field-value-search__selected'>
                        { props.selected }
                    </div>
                )
            }
        </div>
    );
};

// set display name
searchFieldValue.displayName = 'matchCriteria.searchFieldValue';

// set prop types
searchFieldValue.defaultProps = {
    value: [],
    field: {},
};

/*--------*/

// `text` field value view
const textFieldValue = ( props ) => {

    const value = props.value;
    const displayValue = ( 'address' === props.field.name ) ? (
            filter( [ value.line1, value.line2, value.city, value.state, value.zip ], Boolean ).join( ', ' )
        ) : (
            getDisplayValue( { type: props.field.type, value: value } ) );

    return (
        <div { ...props.binding } className='field-values-text'>
            { displayValue }
        </div>
    );
};

// set display name
textFieldValue.displayName = 'matchCriteria.textFieldValue';

// set prop types
textFieldValue.defaultProps = {
    value: '',
    field: {},
};

/*--------*/


// get field value view
const getFieldValueView = ( type ) => {
    switch( type ) {

        // radio selection
        case 'radio': {
            return radioFieldValue;
        }

        // typeahead search
        case 'search': {
            return searchFieldValue;
        }

        // text value
        default: {
            return textFieldValue;
        }
    }
};

/*******************************/

// criteria field view
const CriteriaFieldView = ( props ) => {

    // `yes` and `no` action class name
    const yesActionClassName = classnames( 'auth-flow-criteria-field__selection__choice__item auth-flow-criteria-field__selection__choice__item--yes', {
        'auth-flow-criteria-field__selection__choice__item--active': true === get( props, 'status.result' ),
    } );
    const noActionClassName = classnames( 'auth-flow-criteria-field__selection__choice__item auth-flow-criteria-field__selection__choice__item--no', {
        'auth-flow-criteria-field__selection__choice__item--active': false === get( props, 'status.result' ),
    } );

    // get field value and selected option value
    const fieldValue = props.values[ props.field.name ];

    // get status result and value
    const statusResult = get( props, 'status.result', null );
    const statusValue = get( props, 'status.value', null );

    const isReadonly = get( props.field.config, 'readonly', false );

    return (
        <div className='auth-flow-criteria-field' style={ { width: props.layout[ props.field.name ] } }>

            {/* field title */}
            <div className='auth-flow-criteria-field__title'>
                { getLabel( props.field.title ) }
            </div>

            {/* field value selection */}
            <div className='auth-flow-criteria-field__selection'>

                {/* { don't show choice container is its only readonly } */}
                { ! isReadonly ?

                    // choice container
                    <div className='auth-flow-criteria-field__selection__choice'>
                        {/* check (yes) */}
                        <div
                            className={ yesActionClassName }
                            onClick={ ! includes( [ DATA_TYPES.RADIO, DATA_TYPES.SEARCH ], props.field.type ) ? () => props.onSetChoice( {
                                field: props.field,
                                value: isString( fieldValue ) ? fieldValue : null,
                                result: ( true === statusResult ? null : true ) }
                            ) : null }
                        >
                            <Icon name='checkmark' fill='#ffffff' width='10' height='10'/>
                        </div>

                        {/* cross (no) */}
                        <div
                            className={ noActionClassName }
                            onClick={ () => props.onSetChoice( {
                                field: props.field,
                                value: isString( fieldValue ) ? fieldValue : null,
                                result: ( false === statusResult ? null : false )
                            } ) }
                        >
                            <Icon name='cross' fill='#ffffff' width='10' height='10'/>
                        </div>
                    </div>
                : null  }

                {/* value container */}
                <div className='auth-flow-criteria-field__selection__value-container'>

                    <props.field.view
                        field={ props.field }
                        value={ fieldValue }
                        selected={ statusValue }
                        onSelect={ ( value ) => props.onSetChoice( { field: props.field, value, result: true } ) }
                    />
                    
                    {/* <Tooltip
                        text={ fieldValue }
                        activateOnOverflow={ true }
                        render={ ( binding = {} ) => (
                            
                            <props.field.view
                                binding= { binding }
                                field={ props.field }
                                value={ fieldValue }
                                selected={ statusValue }
                                onSelect={ ( value ) => props.onSetChoice( { field: props.field, value, result: true } ) }
                            />

                        ) }
                    /> */}
                </div>
            </div>
        </div>
    );
};

// set display name
CriteriaFieldView.displayName = 'matchCriteria.criteriaFieldView';

/*******************************/
export const matchCriteria = ( props ) => {

    // dynamic widget class name
    const widgetClassName = classnames( 'ui-match-criteria', {
        'ui-match-criteria--verified': props.verified,
    } );

    /*-----------*/

    // cache critera (fields) and resolve views
    const fields = useMemo( () => {
        return map( props.criteria, field => {
            return( {
                ...field,
                view: getFieldValueView( field.type ),
            } );
        } );
    }, [] );

    /*-----------*/

    // mandatory and optional criteria fields
    const mandatoryFields = filter( fields, field => includes( props.mandatoryCriteria.ids, field.name ) );
    const optionalFields = filter( fields, field => includes( get( props.optionalCriteria, 'ids', {} ), field.name ) );

    /*-----------*/

    // titles of mandatory and optional sections
    const mandatoryTitle = props.mandatoryCriteria.title;

    // mandatory and optional section classnames
    const mandatoryCriteriaSectionClassName = classnames( 'ui-match-criteria__section ui-match-criteria__section--mandatory', {
        'ui-match-criteria__section--satisfied': props.criteriaStatus.mandatory,
    } );
    const optionalCriteriaSectionClassName = classnames( 'ui-match-criteria__section ui-match-criteria__section--optional', {
        'ui-match-criteria__section--satisfied': props.criteriaStatus.optional,
    } );

    return (
        <div className={ widgetClassName }>

            {/* mandatory section */}
            <div className={ mandatoryCriteriaSectionClassName }>
                {/* header */}
                <div className='ui-match-criteria__section__header'>
                    {/* title */}
                    <p className='ui-match-criteria__section__header__title'>
                        { ! isEmpty( mandatoryTitle ) ? getLabel( props.mandatoryCriteria.title ) : null }
                    </p>
                </div>

                {/* field items */}
                <div className='ui-match-criteria__section__items'>
                    {
                        map( mandatoryFields, field => {
                            return (
                                <CriteriaFieldView
                                    { ...props }
                                    key={ field.name }
                                    field={ field }
                                    status={ props.fieldsStatus[ field.name ] }
                                />
                            );
                        } )
                    }
                </div>
            </div>
           { ! isEmpty( optionalFields  ) ?

            // {/* optional section */}
            <div className={ optionalCriteriaSectionClassName }>
                {/* header */}
                <div className='ui-match-criteria__section__header'>
                    {/* title */}
                    <p className='ui-match-criteria__section__header__title'>
                        { getLabel( props.optionalCriteria.title ) } { props.minOptionalCriteriaMatch }/{ size( optionalFields ) }
                    </p>

                    {/* subtitle */}
                    <p className='ui-match-criteria__section__header__title'>
                        { getLabel( props.optionalCriteria.subtitle ) }
                    </p>
                </div>

                {/* field items */}
                <div className='ui-match-criteria__section__items'>
                    {
                        map( optionalFields, field => {
                            return (
                                <CriteriaFieldView
                                    { ...props }
                                    key={ field.name }
                                    field={ field }
                                    status={ props.fieldsStatus[ field.name ] }
                                />
                            );
                        } )
                    }
                </div>
            </div>
            : null
            }
        </div>
    );
};

// set display name
matchCriteria.displayName = 'matchCriteria';

// set default props
matchCriteria.defaultProps = {};

// prop types
matchCriteria.propTypes = {};
